/**
 * 2D array generator nodes for ECS grid layers.
 *
 * Each outputs a number[][] through an internal output.
 * Connect to Grid layer inputs to initialise cell data.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@/graph/Payload';
import { params, internals } from '@/nodes/TypedEndpoints';
import { fbm, makeRng } from '../GridState';

// ─── Noise2D ─────────────────────────────────────────────────

const noiseEndpoints = {
  ...params({
    cols: { value: 20, min: 1, max: 64, step: 1, info: 'Array columns' },
    rows: { value: 20, min: 1, max: 64, step: 1, info: 'Array rows' },
    seed: { value: 42, min: 0, max: 99999, step: 1, info: 'RNG seed' },
    scale: { value: 4, min: 0.1, max: 20, step: 0.1, info: 'Noise scale' },
  }),
  ...internals({
    out: { dataType: 'array', isOutput: true, label: 'Out' },
  }),
};

class Noise2DNode extends BaseNode<typeof noiseEndpoints> {
  initialize(): void {
    super.initialize();
    this.generate();
  }

  onParamChanged(_payload: Payload, _endpointId: string): void {
    this.generate();
  }

  private generate(): void {
    const cols = this.params.cols.value;
    const rows = this.params.rows.value;
    const seed = this.params.seed.value;
    const scale = this.params.scale.value;

    const result: number[][] = [];
    for (let r = 0; r < rows; r++) {
      const row: number[] = [];
      for (let c = 0; c < cols; c++) {
        row.push(fbm(c / scale, r / scale, seed));
      }
      result.push(row);
    }
    this.internals.out.set(result);
  }
}

export const noise2DNodeDef: NodeDef<Noise2DNode> = {
  type: 'Noise2D',
  shortName: 'noise2d',
  label: 'Noise 2D',
  info: 'Generates a 2D array of Perlin noise. Connect to a Grid layer input.',
  domain: Domain.Data,
  endpoints: noiseEndpoints,
  ctor: Noise2DNode,
    category: 'generator'
};

// ─── Gridlines2D ─────────────────────────────────────────────

const gridlinesEndpoints = {
  ...params({
    cols: { value: 20, min: 1, max: 64, step: 1, info: 'Array columns' },
    rows: { value: 20, min: 1, max: 64, step: 1, info: 'Array rows' },
    divX: { dataType: 'array', value: [1, 1, 1, 1], min: -1, max: 10, step: 1, length: 8, info: 'Proportional column widths. Gridlines appear at boundaries.' },
    divY: { dataType: 'array', value: [1, 1, 1, 1], min: -1, max: 10, step: 1, length: 8, info: 'Proportional row heights. Gridlines appear at boundaries.' },
  }),
  ...internals({
    out: { dataType: 'array', isOutput: true, label: 'Out' },
  }),
};

class Gridlines2DNode extends BaseNode<typeof gridlinesEndpoints> {
  initialize(): void {
    super.initialize();
    this.generate();
  }

  onParamChanged(_payload: Payload, _endpointId: string): void {
    this.generate();
  }

  private generate(): void {
    const cols = this.params.cols.value;
    const rows = this.params.rows.value;
    const divX = this.toPositiveArray(this.params.divX.value);
    const divY = this.toPositiveArray(this.params.divY.value);

    // Compute boundary columns/rows from proportional divisions
    const vertLines = this.boundarySet(divX, cols);
    const horizLines = this.boundarySet(divY, rows);

    const result: number[][] = [];
    for (let r = 0; r < rows; r++) {
      const row: number[] = [];
      for (let c = 0; c < cols; c++) {
        row.push((vertLines.has(c) || horizLines.has(r)) ? 1 : 0);
      }
      result.push(row);
    }
    this.internals.out.set(result);
  }

  /** Convert proportional divisions to a set of boundary cell indices (excluding edges). */
  private boundarySet(divs: number[], size: number): Set<number> {
    const set = new Set<number>();
    if (divs.length <= 1) return set;
    const sum = divs.reduce((a, b) => a + b, 0);
    if (sum <= 0) return set;
    let cumulative = 0;
    for (let i = 0; i < divs.length - 1; i++) {
      cumulative += divs[i];
      const boundary = Math.round((cumulative / sum) * size);
      if (boundary > 0 && boundary < size) set.add(boundary);
    }
    return set;
  }

  private toPositiveArray(value: unknown): number[] {
    if (!Array.isArray(value)) return [];
    return value.filter(v => typeof v === 'number' && isFinite(v) && v > 0);
  }
}

export const gridlines2DNodeDef: NodeDef<Gridlines2DNode> = {
  type: 'Gridlines2D',
  shortName: 'gridlines',
  label: 'Gridlines 2D',
  info: 'Generates a 2D array with 1s on gridlines, 0s elsewhere. Connect to a Grid layer input.',
  domain: Domain.Data,
  endpoints: gridlinesEndpoints,
  ctor: Gridlines2DNode,
    category: 'generator'
};

// ─── Random2D ────────────────────────────────────────────────

const random2DEndpoints = {
  ...params({
    cols: { value: 20, min: 1, max: 64, step: 1, info: 'Array columns' },
    rows: { value: 20, min: 1, max: 64, step: 1, info: 'Array rows' },
    seed: { value: 42, min: 0, max: 99999, step: 1, info: 'RNG seed' },
    min: { value: 0, min: -10, max: 10, step: 0.01, info: 'Min value' },
    max: { value: 1, min: -10, max: 10, step: 0.01, info: 'Max value' },
  }),
  ...internals({
    out: { dataType: 'array', isOutput: true, label: 'Out' },
  }),
};

class Random2DNode extends BaseNode<typeof random2DEndpoints> {
  initialize(): void {
    super.initialize();
    this.generate();
  }

  onParamChanged(_payload: Payload, _endpointId: string): void {
    this.generate();
  }

  private generate(): void {
    const cols = this.params.cols.value;
    const rows = this.params.rows.value;
    const rng = makeRng(this.params.seed.value);
    const lo = this.params.min.value;
    const hi = this.params.max.value;

    const result: number[][] = [];
    for (let r = 0; r < rows; r++) {
      const row: number[] = [];
      for (let c = 0; c < cols; c++) {
        row.push(lo + rng() * (hi - lo));
      }
      result.push(row);
    }
    this.internals.out.set(result);
  }
}

export const random2DNodeDef: NodeDef<Random2DNode> = {
  type: 'Random2D',
  shortName: 'random2d',
  label: 'Random 2D',
  info: 'Generates a 2D array of random values in [min, max]. Connect to a Grid layer input.',
  domain: Domain.Data,
  endpoints: random2DEndpoints,
    category: 'generator',
  ctor: Random2DNode,
};

// ─── Gradient2D ─────────────────────────────────────────────

const gradient2DEndpoints = {
  ...params({
    cols: { value: 20, min: 1, max: 128, step: 1, info: 'Array columns' },
    rows: { value: 20, min: 1, max: 128, step: 1, info: 'Array rows' },
    direction: { dataType: 'string', value: 'left-right', options: ['left-right', 'right-left', 'top-bottom', 'bottom-top'] as const, info: 'Gradient direction (high → low)' },
    low: { value: 0, min: -10, max: 10, step: 0.01, info: 'Low end value' },
    high: { value: 1, min: -10, max: 10, step: 0.01, info: 'High end value' },
    noise: { value: 0, min: 0, max: 1, step: 0.01, info: 'Noise amount to add' },
    seed: { value: 42, min: 0, max: 99999, step: 1, info: 'RNG seed for noise' },
  }),
  ...internals({
    out: { dataType: 'array', isOutput: true, label: 'Out' },
  }),
};

class Gradient2DNode extends BaseNode<typeof gradient2DEndpoints> {
  initialize(): void {
    super.initialize();
    this.generate();
  }

  onParamChanged(_payload: Payload, _endpointId: string): void {
    this.generate();
  }

  private generate(): void {
    const cols = this.params.cols.value;
    const rows = this.params.rows.value;
    const dir = this.params.direction.value as string;
    const lo = this.params.low.value;
    const hi = this.params.high.value;
    const noiseAmt = this.params.noise.value;
    const seed = this.params.seed.value;
    const scale = 4; // noise scale

    const result: number[][] = [];
    for (let r = 0; r < rows; r++) {
      const row: number[] = [];
      for (let c = 0; c < cols; c++) {
        let t: number;
        switch (dir) {
          case 'left-right': t = c / (cols - 1); break;
          case 'right-left': t = 1 - c / (cols - 1); break;
          case 'top-bottom': t = r / (rows - 1); break;
          case 'bottom-top': t = 1 - r / (rows - 1); break;
          default: t = c / (cols - 1);
        }
        const base = lo + t * (hi - lo);
        const n = noiseAmt > 0 ? fbm(c / scale, r / scale, seed) * noiseAmt : 0;
        row.push(base + n);
      }
      result.push(row);
    }
    this.internals.out.set(result);
  }
}

export const gradient2DNodeDef: NodeDef<Gradient2DNode> = {
  type: 'Gradient2D',
  shortName: 'gradient2d',
  label: 'Gradient 2D',
  info: 'Generates a 2D array with a linear gradient. Connect to a Grid layer input.',
  domain: Domain.Data,
  endpoints: gradient2DEndpoints,
  category: 'generator',
  ctor: Gradient2DNode,
};
